import U2000InputText from "../../../../u2000Core/components/formControls/input/U2000InputText";
import type { U2000FormStateHandlers } from "../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import type { TFunction } from "i18next";
import { NS } from "../../../U2104";
import U2000Panel from "../../../../u2000Core/components/layout/U2000Panel";
import U2000CheckBox from "../../../../u2000Core/components/formControls/checkbox/U2000CheckBox";
import U2000PasswordProgressionPopup from "../../../../u2000Core/components/popup/U2000PasswordProgressionPopup";
import { U2000FormStateEnumEditMode } from "../../../../u2000Core/reducer/formReducer/U2000FormState";
import { useState } from "react";

// Typage des props du composant
interface U2104AffiliationComponentProps<T extends Record<string, any>> {
  formStateHandlers: U2000FormStateHandlers<T>;
  t: TFunction;
}

// Composant principal pour la gestion des informations d'une affiliation
function U2104AffiliationBody<T extends Record<string, any>>({ formStateHandlers, t }: U2104AffiliationComponentProps<T>) {
  const resetPwd = "resetPwd" in formStateHandlers.getCustomAttributes();
  const [showPasswordPopup, setShowPasswordPopup] = useState(false);

  return (
    <div className="flex h-full items-stretch gap-4">
      {/* Colonne gauche (66%) */}
      <div className="flex flex-[66_66_0%] flex-col gap-4">
        <U2000Panel title={t("user.lblGeneralInfo", { ns: NS })} titleAlign="left" className="flex h-full w-full flex-col items-start justify-start">
          {/* Ligne 1: Username */}
          <div className="mt-3 flex w-full gap-4">
            <div className="flex-1">
              <U2000InputText
                fieldName="user.username"
                label={t("user.lblUsername", { ns: NS })}
                formStateHandlers={formStateHandlers}
                readonly={formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.EDIT}
              />
            </div>
            <div className="flex-1">
              {(formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.ADD || resetPwd) && (
                <div className="relative">
                  <U2000InputText
                    fieldName="user.password"
                    label={t("user.lblPassword", { ns: NS })}
                    formStateHandlers={formStateHandlers}
                    onFocus={() => setShowPasswordPopup(true)}
                    onBlur={() => setShowPasswordPopup(false)}
                  />
                  {/* Password strength popup parfaitement alignée à l'input */}
                  <U2000PasswordProgressionPopup password={formStateHandlers.getData().user.password} show={showPasswordPopup} position="BOTTOM_CENTER" />
                </div>
              )}
            </div>
          </div>
          {/* Ligne 2: Firstname et Lastname */}
          <div className="mt-3 flex w-full gap-4">
            <div className="flex-1">
              <U2000InputText fieldName="user.firstName" label={t("user.lblFirstName", { ns: NS })} formStateHandlers={formStateHandlers} readonly={resetPwd} />
            </div>
            <div className="flex-1">
              <U2000InputText fieldName="user.lastName" label={t("user.lblLastName", { ns: NS })} formStateHandlers={formStateHandlers} readonly={resetPwd} />
            </div>
          </div>
          {/* Ligne 3: Email et Password */}
          <div className="mt-3 flex w-full gap-4">
            <div className="flex-1">
              <U2000InputText fieldName="user.email" label={t("user.lblEmail", { ns: NS })} formStateHandlers={formStateHandlers} readonly={resetPwd} />
            </div>
          </div>
        </U2000Panel>
      </div>

      {/* Colonne droite (34%) */}
      <div className="flex flex-[34_34_0%]">
        <U2000Panel title={t("user.lblConnectInfo", { ns: NS })} titleAlign="left" className="flex h-full w-full flex-col items-start justify-start">
          <div className="mt-3 flex w-full justify-start gap-2 px-2">
            <div className="mt-5 flex-1">
              <U2000CheckBox formStateHandlers={formStateHandlers} fieldName="user.active" label={t("user.lblChkActive", { ns: NS })} readonly={resetPwd} />
            </div>
            <div className="flex-2">
              <U2000InputText fieldName="user.lastLogin" label={t("user.lblLastLogin", { ns: NS })} formStateHandlers={formStateHandlers} readonly={true} />
            </div>
          </div>
          <div className="mt-3 flex w-full justify-start gap-2 px-2">
            <div className="mt-5 flex-1">
              <U2000CheckBox formStateHandlers={formStateHandlers} fieldName="user.locked" label={t("user.lblChkLocked", { ns: NS })} />
            </div>
            <div className="flex-2">
              <U2000InputText type="number" fieldName="nbOfFailedAttempts" label={t("user.lblNbOfFailedAttempts", { ns: NS })} formStateHandlers={formStateHandlers} readonly={true} />
            </div>
          </div>
          <div className="mt-3 flex w-full justify-start gap-2 px-2">
            <div className="mt-5 flex-1">
              <U2000CheckBox formStateHandlers={formStateHandlers} fieldName="user.changePasswordOnLogin" label={t("user.lblChkChangePasswordOnLogin", { ns: NS })} />
            </div>
          </div>
        </U2000Panel>
      </div>
    </div>
  );
}

export default U2104AffiliationBody;
